import { type ReactElement } from 'react'

import { ButtonRoot } from '@/components/common/Button'
import { FloatingPopover } from '@/components/common/Floating'
import { LinkOutbound } from '@/components/common/Link'
import { type RecType } from '@/defs/rec'
import { useReduxDispatch } from '@/store/hooks'
import { toastActions } from '@/store/toast'
import { trackEvent } from '@/utils/analyticsUtil'
import { getBulkExportLink } from '@/utils/brandUtil'

import styles from './DecklistExport.module.scss'

type Props = {
  analyticsCategory?: string
  children: ReactElement
  decklist: string[]
  disabled?: boolean
  recs: RecType[]
}

export const DecklistExport = ({
  analyticsCategory = 'decklist_export',
  children,
  decklist,
  disabled,
  recs,
}: Props) => {
  const dispatch = useReduxDispatch()

  const onClick = (label: string) => {
    trackEvent({
      action: `${analyticsCategory}_export`,
      category: analyticsCategory,
      label,
    })
  }

  const onExportPlainText = () => {
    const total = recs.reduce((sum, r) => sum + (r.count || 1), 0)
    const plainText = recs.map((rec) => `${rec.count || 1} ${rec.name}`).join('\r\n')
    navigator.clipboard.writeText(plainText)
    dispatch(
      toastActions.add(
        <p>
          Copied <strong>{total} cards</strong> to clipboard
        </p>,
      ),
    )
    onClick('Plain Text')
  }

  const archidektHref = getBulkExportLink({ brand: 'archidekt', decklist })
  const moxfieldHref = getBulkExportLink({ brand: 'moxfield', decklist })

  const tooltipElement = !disabled && (
    <ul className={styles.dropdown}>
      <li>
        <LinkOutbound
          className={styles.item}
          href={archidektHref}
          onClick={() => onClick('Archidekt')}
          utm={{ medium: 'decklist' }}
        >
          Archidekt
        </LinkOutbound>
      </li>
      <li>
        <LinkOutbound
          className={styles.item}
          href={moxfieldHref}
          onClick={() => onClick('Moxfield')}
          utm={{ medium: 'decklist' }}
        >
          Moxfield
        </LinkOutbound>
      </li>
      <li>
        <ButtonRoot className={styles.item} onClick={onExportPlainText}>
          Plain Text
        </ButtonRoot>
      </li>
    </ul>
  )

  return <FloatingPopover content={tooltipElement}>{children}</FloatingPopover>
}
